import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { UserAvatarGroup } from '../../components/UserAvatar';
import PortfolioPermission from '../../components/PortfolioPermission';
import './PortfolioTable.css';

const PortfolioTable = ({
	portfolios,
	onView,
	onEdit,
	onDelete,
	isDeleting,
}) => {
	const [openDropdown, setOpenDropdown] = useState(null);
	const [dropdownCoords, setDropdownCoords] = useState(null);
	const buttonRefs = useRef({});
	const menuRef = useRef(null);

	// Close dropdown when clicking outside or scrolling/resizing
	useEffect(() => {
		if (!openDropdown) return;

		const handleClose = () => {
			setOpenDropdown(null);
			setDropdownCoords(null);
		};

		const handleClickOutside = (event) => {
			if (
				menuRef.current &&
				!menuRef.current.contains(event.target) &&
				!buttonRefs.current[openDropdown]?.contains(event.target)
			) {
				handleClose();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		window.addEventListener('scroll', handleClose, true);
		window.addEventListener('resize', handleClose);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			window.removeEventListener('scroll', handleClose, true);
			window.removeEventListener('resize', handleClose);
		};
	}, [openDropdown]);

	const formatDate = (dateString) => {
		if (!dateString) return '—';
		return moment(dateString).format('MM/DD/YYYY');
	};

	const toggleDropdown = (portfolioId, e) => {
		e.stopPropagation();
		if (openDropdown === portfolioId) {
			setOpenDropdown(null);
			setDropdownCoords(null);
		} else {
			const btn = buttonRefs.current[portfolioId];
			if (btn) {
				const rect = btn.getBoundingClientRect ? btn.getBoundingClientRect() : { top: 0, bottom: 0, left: 0, right: 0 };
				const menuWidth = 140;
				const menuHeight = 115;
				const spaceBelow = window.innerHeight ? window.innerHeight - rect.bottom : 200;
				const openUpwards = spaceBelow < menuHeight && rect.top > menuHeight;

				const top = openUpwards ? Math.max(0, rect.top - menuHeight - 4) : rect.bottom + 4;
				const left = Math.max(0, rect.right - menuWidth);

				setDropdownCoords({ top, left });
			} else {
				setDropdownCoords({ top: 0, left: 0 });
			}
			setOpenDropdown(portfolioId);
		}
	};

	const getPortfolioUsers = (portfolio) => {
		return portfolio.users && portfolio.users.length > 0 ? portfolio.users : [];
	};

	const activePortfolio = portfolios?.find((p) => p.id === openDropdown);

	return (
		<div className="portfolio-table-responsive table-responsive">
			<table className="table portfolio-table">
				<thead>
					<tr>
						<th scope="col" className="date-col">Date Created</th>
						<th scope="col" className="portfolio-name-col">Portfolio Name</th>
						<th scope="col" className="company-name-col">Company Name</th>
						<th scope="col" className="properties-col">Properties</th>
						<th scope="col" className="alerts-col">Alerts</th>
						<th scope="col" className="certifications-col">Certifications</th>
						<th scope="col" className="export-col">Export</th>
						<th scope="col" className="users-col">Users</th>
						<th scope="col" className="actions-col">Actions</th>
					</tr>
				</thead>
				<tbody>
					{(!portfolios || portfolios.length === 0) ? (
						<tr>
							<td colSpan={9} className="text-center py-4 text-muted">
								No portfolios found
							</td>
						</tr>
					) : (
						portfolios.map((portfolio) => {
							const portfolioUsers = getPortfolioUsers(portfolio);

							return (
								<tr
									key={`tb-row-${portfolio.id}`}
									className="portfolio-table-row"
								>
									{/* Date Created */}
									<td className="date-cell">
										{formatDate(portfolio.created_at || portfolio.createdAt)}
									</td>

									{/* Portfolio Name */}
									<td className="portfolio-name-cell">
										<button
											type="button"
											className="portfolio-name-link"
											onClick={() => onView(portfolio.id)}
										>
											{portfolio.name}
										</button>
									</td>

									{/* Company Name */}
									<td className="company-name-cell">
										{portfolio.company || portfolio.companyName || portfolio.organization?.name || '—'}
									</td>

									{/* Properties */}
									<td className="properties-cell">
										{portfolio.propertyCount || 0}
									</td>

									{/* Alerts */}
									<td className="alerts-cell">
										{portfolio.openViolationsCount ?? portfolio.alertsCount ?? 0}
									</td>

									{/* Certifications */}
									<td className="certifications-cell">
										{portfolio.certificatesCount || 0}
									</td>

									{/* Export */}
									<td className="export-cell">
										{portfolio.uploadsCount || 0}
									</td>

									{/* Users */}
									<td className="users-cell">
										<UserAvatarGroup
											referenceId={`tb-tr-${portfolio.id}`}
											users={portfolioUsers}
											maxVisible={3}
											size="medium"
											showTooltip={true}
											onUserClick={(user) => {}}
											onMoreClick={() => {}}
										/>
									</td>

									{/* Actions */}
									<td className="actions-cell">
										<button
											type="button"
											className={`actions-toggle ${openDropdown === portfolio.id ? 'active' : ''}`}
											ref={(el) => (buttonRefs.current[portfolio.id] = el)}
											onClick={(e) => toggleDropdown(portfolio.id, e)}
											disabled={isDeleting}
											aria-label="Actions menu"
										>
											<i className="bi bi-three-dots-vertical" />
										</button>
									</td>
								</tr>
							);
						})
					)}
				</tbody>
			</table>

			{/* Actions Dropdown Menu - rendered in Portal on document.body so table has zero scroll */}
			{openDropdown && dropdownCoords && activePortfolio &&
				createPortal(
					<div
						ref={menuRef}
						className="portfolio-table-portal-menu"
						style={{
							position: 'fixed',
							top: `${dropdownCoords.top}px`,
							left: `${dropdownCoords.left}px`,
							zIndex: 99999,
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<button
							type="button"
							className="actions-item"
							onClick={() => {
								onView(activePortfolio.id);
								setOpenDropdown(null);
								setDropdownCoords(null);
							}}
						>
							<i className="bi bi-eye me-2" />
							View
						</button>
						<PortfolioPermission
							portfolio={activePortfolio}
							requiredPermission="admin"
						>
							<button
								type="button"
								className="actions-item"
								onClick={() => {
									onEdit(activePortfolio.id);
									setOpenDropdown(null);
									setDropdownCoords(null);
								}}
							>
								<i className="bi bi-pencil me-2" />
								Edit
							</button>
							<hr className="actions-divider" />
							<button
								type="button"
								className="actions-item actions-item-danger"
								onClick={() => {
									onDelete(activePortfolio.id);
									setOpenDropdown(null);
									setDropdownCoords(null);
								}}
								disabled={isDeleting}
							>
								<i className="bi bi-trash me-2" />
								{isDeleting ? 'Deleting...' : 'Delete'}
							</button>
						</PortfolioPermission>
					</div>,
					document.body,
				)}
		</div>
	);
};

export default PortfolioTable;
